import React, { useState } from 'react';
import { 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  Typography,
  Box,
  IconButton,
  Drawer,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { 
  Dashboard as DashboardIcon,
  Web as WebIcon,
  BarChart as AnalyticsIcon,
  Settings as SettingsIcon,
  AutoFixHigh as AIIcon,
  VideoCall as VideoIcon,
  Image as ImageIcon,
  Payments as PaymentsIcon,
  Inventory as InventoryIcon,
  Description as ContentIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

function AccountSidebar({ accountId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: `/accounts/${accountId}/dashboard`,
    },
    {
      text: 'Websites',
      icon: <WebIcon />,
      path: `/accounts/${accountId}/sites`,
    },
    {
      text: 'Products & Inventory',
      icon: <InventoryIcon />,
      path: `/accounts/${accountId}/products`,
    },
    {
      text: 'AI Media',
      icon: <ImageIcon />,
      path: `/accounts/${accountId}/ai-media`,
    },
    {
      text: 'AI Content',
      icon: <ContentIcon />,
      path: `/accounts/${accountId}/ai-content`,
    },
    {
      text: 'AI Usage',
      icon: <AnalyticsIcon />,
      path: `/accounts/${accountId}/ai-usage`,
    },
    {
      text: 'Billing',
      icon: <PaymentsIcon />,
      path: `/accounts/${accountId}/billing`,
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: `/accounts/${accountId}/settings`,
    },
  ];

  const drawer = (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
          Account Menu
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <List component="nav">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem 
              button 
              component={Link} 
              to={item.path} 
              key={item.text}
              sx={{ 
                borderRadius: 1,
                mb: 0.5,
                backgroundColor: isActive ? 'rgba(92, 45, 145, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: isActive ? 'rgba(92, 45, 145, 0.12)' : 'rgba(92, 45, 145, 0.08)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontWeight: isActive ? 'bold' : 'normal',
                  color: isActive ? 'primary.main' : 'inherit'
                }} 
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  // For mobile, we'll show a menu button that opens a drawer
  if (isMobile) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Tooltip title="Menu">
            <IconButton 
              color="primary" 
              aria-label="open menu" 
              onClick={handleDrawerToggle}
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            '& .MuiDrawer-paper': { 
              width: 280,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>
      </>
    );
  }

  // For desktop, we'll show the sidebar directly
  return drawer;
}

export default AccountSidebar; 